import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";

const LiveSlideshow = ({ eventId, onClose }) => {
  const [media, setMedia] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isStarted, setIsStarted] = useState(false);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await api.get(`/api/media/event/${eventId}?status=approved`);
      setMedia(res.data);
    } catch (err) {
      console.error("Slideshow fetch error:", err);
    }
  }, [eventId]);

  useEffect(() => {
    fetchMedia();
    const fetchInterval = setInterval(fetchMedia, 30000); // Check for new media every 30s
    return () => clearInterval(fetchInterval);
  }, [fetchMedia]);

  useEffect(() => {
    if (media.length > 0 && isStarted) {
      // For photos and text, use a 5-second timer. For video and voice, we wait for 'onEnded'.
      const current = media[currentIndex];
      if (current.type === "photo" || current.type === "text") {
        const timer = setTimeout(() => {
          setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
        }, 5000); // Change every 5s
        return () => clearTimeout(timer);
      }
    }
  }, [currentIndex, media, isStarted]);

  if (media.length === 0) {
    return (
      <div className="slideshow-overlay bg-dark text-white d-flex flex-column align-items-center justify-content-center p-4 text-center" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 9999 }}>
        <h2 className="mb-4 fw-black" style={{ fontFamily: 'var(--font-headline)' }}>لسه مفيش صور اتوافقت عليها للعرض.</h2>
        <button className="btn btn-outline-light px-5 py-3 rounded-pill fw-bold" onClick={onClose}>خروج من العرض الحي</button>
      </div>
    );
  }

  if (!isStarted) {
    return (
      <div className="slideshow-overlay bg-black text-white d-flex flex-column align-items-center justify-content-center" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 9999 }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center px-4"
        >
          <h1 className="display-3 fw-black mb-5" style={{ fontFamily: 'var(--font-headline)' }}>مستعد تبدأ العرض؟ 📽️</h1>
          <button
            className="btn btn-primary btn-lg px-5 py-3 rounded-pill fw-black fs-2 shadow-lg"
            style={{ fontFamily: 'var(--font-headline)' }}
            onClick={() => setIsStarted(true)}
          >
            ابدأ الآن ✨
          </button>
          <p className="mt-4 text-muted fs-5 arabic-leading">العرض هيشغل الصور والفيديوهات تلقائياً مع الصوت.</p>
        </motion.div>
      </div>
    );
  }

  const currentMedia = media[currentIndex];

  return (
    <motion.div
      className="slideshow-overlay bg-black text-white"
      style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 9999 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <button
        className="btn btn-dark btn-lg p-3 position-absolute top-0 end-0 m-2 border-0"
        style={{ zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      >
        ✕ خروج
      </button>

      <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4">
        <div className="media-container text-center w-100 h-75 d-flex align-items-center justify-content-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentMedia._id}
              initial={{ opacity: 0, scale: 0.8, x: 100 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 1.2, x: -100 }}
              transition={{ duration: 0.8 }}
              className="h-100 w-100 d-flex align-items-center justify-content-center"
            >
              {currentMedia.type === "photo" && (
                <img src={currentMedia.url} alt="Slideshow" className="img-fluid h-100 rounded shadow-lg" style={{ objectFit: "contain", maxWidth: "100%", maxHeight: "100%" }} />
              )}
              {currentMedia.type === "video" && (
                <video
                  src={currentMedia.url}
                  autoPlay
                  className="h-100 rounded shadow-lg"
                  style={{ objectFit: "contain", maxWidth: "100%", maxHeight: "100%" }}
                  onEnded={() => setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length)}
                />
              )}
              {currentMedia.type === "voice" && (
                <div className="text-center">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="display-1 p-5 rounded-circle bg-primary shadow-lg border border-3 border-white mb-4 d-inline-block"
                  >
                    🎙️
                  </motion.div>
                  <audio
                    src={currentMedia.url}
                    autoPlay
                    onEnded={() => setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length)}
                  />
                  <h3 className="fw-black mt-3 display-5" style={{ fontFamily: 'var(--font-headline)' }}>رسالة صوتية من {currentMedia.guestName}</h3>
                </div>
              )}
              {currentMedia.type === "text" && (
                <div className="p-5 bg-white text-dark rounded-celebration shadow-lg w-100" style={{ maxWidth: "800px" }}>
                  <p className="display-4 fw-black mb-0 text-break arabic-leading" style={{ fontFamily: 'var(--font-headline)' }}>"{currentMedia.content}"</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.div
          className="mt-4 text-center"
          key={`info-${currentMedia._id}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="display-5 fw-black mb-1 shadow-sm" style={{ fontFamily: 'var(--font-headline)' }}>{currentMedia.guestName}</h2>
          <div className="badge bg-primary fs-5 px-4 py-2 rounded-pill shadow-lg mt-2">
            ❤️ {typeof currentMedia.reactions === 'number'
                ? currentMedia.reactions
                : (currentMedia.reactions?.heart || 0)}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LiveSlideshow;
